import axios from "axios"

const API_BASE_URL = process.env.REACT_APP_DATABASEURL || "http://nextgen-dairy.receptumelogic.com/api/public/api/"

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 second timeout — prevents UI freeze on dead connections
})

// ── Performance Logging ──
const PERF_ENABLED = true // Set to false in production
const SLOW_THRESHOLD_MS = 1000 // Warn if response takes longer than this

// Global response interceptor — handles all API errors consistently
api.interceptors.response.use(
  (response) => {
    // Performance timing — successful responses
    if (PERF_ENABLED && response.config._startTime) {
      const elapsed = Date.now() - response.config._startTime
      const color = elapsed > SLOW_THRESHOLD_MS ? '#ef4444' : '#22c55e'
      const icon = elapsed > SLOW_THRESHOLD_MS ? '🐢' : '✅'
      console.log(
        `%c${icon} API ✓ ${response.config.method?.toUpperCase()} ${response.config.url} — ${elapsed}ms [${response.status}]`,
        `color: ${color}; font-weight: bold`
      )
      if (elapsed > SLOW_THRESHOLD_MS) {
        console.warn(`⚠️ SLOW API: ${response.config.url} took ${elapsed}ms (threshold: ${SLOW_THRESHOLD_MS}ms)`)
      }
    }
    return response
  },
  (error) => {
    // Performance timing — error responses
    if (PERF_ENABLED && error.config?._startTime) {
      const elapsed = Date.now() - error.config._startTime
      console.error(
        `%c❌ API ✗ ${error.config?.method?.toUpperCase()} ${error.config?.url} — ${elapsed}ms [${error.response?.status || 'NETWORK ERROR'}]`,
        'color: #ef4444; font-weight: bold'
      )
    }

    // Request was cancelled
    if (axios.isCancel(error)) {
      return Promise.reject({ message: "Request was cancelled", isCancelled: true })
    }

    // Request timed out
    if (error.code === "ECONNABORTED") {
      return Promise.reject({
        message: "Request timed out. Please check your connection and try again.",
        isTimeout: true,
      })
    }

    // Network error (no response received)
    if (!error.response) {
      console.error("Network error — please check your connection")
      return Promise.reject({
        message: "Network error. Please check your internet connection.",
        isNetworkError: true,
      })
    }

    const { status, data } = error.response

    // Authentication expired or unauthorized
    if (status === 401) {
      // Only redirect if not already on login page (prevents loop)
      if (!window.location.pathname.includes('/login')) {
        try { localStorage.removeItem("authUser") } catch (e) { /* ignore */ }
        try { localStorage.removeItem("dairy_role") } catch (e) { /* ignore */ }
        window.location.href = "/login"
      }
      return Promise.reject({ message: "Session expired. Please login again." })
    }

    // Forbidden
    if (status === 403) {
      return Promise.reject({ message: "You don't have permission to perform this action." })
    }

    // Not found
    if (status === 404) {
      console.error(`🚨 404 NOT FOUND: ${error.config?.method?.toUpperCase()} ${error.config?.baseURL}${error.config?.url}`)
      console.error(`   → Check: Is Laravel running? (php artisan serve --port=8000)`)
      console.error(`   → Check: Is the route defined in api.php?`)
      console.error(`   → Full URL: ${error.config?.baseURL}${error.config?.url}`)
      return Promise.reject({ message: data?.message || `API endpoint not found: ${error.config?.url}` })
    }

    // Validation errors (Laravel returns 422)
    if (status === 422) {
      const validationErrors = data?.errors
        ? Object.values(data.errors).flat().join(", ")
        : data?.message || "Validation failed"
      return Promise.reject({ message: validationErrors, isValidation: true })
    }

    // Rate limited (429)
    if (status === 429) {
      return Promise.reject({ message: "Too many requests. Please wait a moment and try again." })
    }

    // Server error
    if (status >= 500) {
      console.error("Server error:", data)
      return Promise.reject({ message: "Server error. Please try again later." })
    }

    // Other errors
    return Promise.reject({
      message: data?.message || "Something went wrong",
      status: status,
    })
  }
)

// Request interceptor — auto-attach auth token + start timing
api.interceptors.request.use(
  (config) => {
    // Performance timing
    if (PERF_ENABLED) {
      config._startTime = Date.now()
      console.log(`%c🔵 API ➤ ${config.method?.toUpperCase()} ${config.url}`, 'color: #6366f1; font-weight: bold')
    }

    try {
      const userData = localStorage.getItem("authUser")
      if (userData) {
        const user = JSON.parse(userData)
        if (user && user.token) {
          config.headers.Authorization = `Bearer ${user.token}`
        }
      }
    } catch (e) {
      // Corrupted localStorage data — clear it and redirect to login
      console.error("Corrupted auth data in localStorage, clearing...", e)
      try { localStorage.removeItem("authUser") } catch (ex) { /* ignore */ }
      try { localStorage.removeItem("dairy_role") } catch (ex) { /* ignore */ }
    }
    return config
  },
  (error) => Promise.reject(error)
)

export default api
